import React, { useMemo, useState } from 'react';

import { compareSortKeys } from '@aline/common';
import { Typography } from '@mui/material';
import {
  gridFilterModelSelector,
  useGridApiContext,
  useGridSelector,
} from '@mui/x-data-grid-premium';
import { Apollo } from 'src/gql/generated';

import { FilterPopoverContent } from './FilterPopoverContent';
import type { FilterContentProps } from './types';
import { ProgressiveColumnIcon } from '../../board/settings/ColumnItem';

export function ColumnFilterContent({ field, operator }: FilterContentProps): JSX.Element {
  const apiRef = useGridApiContext();
  const { data } = Apollo.useGetBoardsQuery();
  const [searchTerm, setSearchTerm] = useState('');
  const filterModel = useGridSelector(apiRef, gridFilterModelSelector);

  const options = useMemo(() => {
    const boards = data?.boards ?? [];
    return boards.flatMap(board => {
      const startedColumns = (board.columns ?? [])
        .filter(c => c.category === Apollo.ColumnCategory.Started)
        .sort((a, b) => compareSortKeys(a.sortKey, b.sortKey));
      return (board.columns ?? []).map(col => {
        const startedIdx = startedColumns.findIndex(c => c.id === col.id);
        return {
          boardName: board.name,
          category: col.category,
          color: col.color,
          id: col.id,
          index: startedIdx >= 0 ? startedIdx + 1 : 1,
          label: col.name,
          total: startedColumns.length || 1,
        };
      });
    });
  }, [data?.boards]);

  const selectedIds = useMemo(() => {
    const filterItem = filterModel.items.find(item => item.field === field);
    const rawValues = filterItem?.value ?? [];
    return new Set(Array.isArray(rawValues) ? rawValues.map((v: { id: string }) => v.id) : []);
  }, [filterModel, field]);

  const handleToggle = (columnId: string) => {
    const column = options.find(option => option.id === columnId);
    if (!column) return;

    const currentValues = filterModel.items.find(item => item.field === field)?.value ?? [];
    const isSelected = selectedIds.has(columnId);

    // The Filters panel renders these values as tags, so they need the full column, not just its id.
    const newValues = isSelected
      ? currentValues.filter((v: { id: string }) => v.id !== columnId)
      : [
          ...currentValues,
          {
            __typename: 'Column',
            boardName: column.boardName,
            category: column.category,
            color: column.color,
            id: column.id,
            index: column.index,
            name: column.label,
            total: column.total,
          },
        ];
    const updatedItems = filterModel.items.filter(item => item.field !== field);
    if (newValues.length > 0) updatedItems.push({ field, operator, value: newValues });

    apiRef.current.setFilterModel({ ...filterModel, items: updatedItems });
  };

  return (
    <FilterPopoverContent
      showSearch
      options={options}
      renderOption={option => (
        <>
          <ProgressiveColumnIcon
            category={option.category}
            color={option.color}
            iconSize={16}
            index={option.index}
            total={option.total}
          />
          <Typography variant="body2">{option.label}</Typography>
        </>
      )}
      searchPlaceholder="Search statuses..."
      searchTerm={searchTerm}
      selectedIds={selectedIds}
      onSearchChange={setSearchTerm}
      onToggle={handleToggle}
    />
  );
}
